/**
 * Web session token.
 *
 * Outside Telegram there is no initData, so the browser signs in once and
 * keeps a bearer token. Wallet seed is created or imported locally (same as
 * TMA) and encrypted with the server-held seed key.
 */

const KEY = 'incpt.webSession';

export function readWebSession(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function storeWebSession(token: string): void {
  try {
    localStorage.setItem(KEY, token);
  } catch {
    /* private mode / quota — the session just won't survive a reload */
  }
}

export function clearWebSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

/** True when the app is running on a web session rather than inside Telegram. */
export const isWebSession = (): boolean => !window.Telegram?.WebApp?.initData && !!readWebSession();

/** TMA initData or a stored web bearer — use instead of truthy-checking initData. */
export function hasApiAuth(initData?: string | null): boolean {
  return !!initData || !!readWebSession();
}
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000';

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text().catch(() => '');
    }
    throw new Error(`API ${res.status} ${path}: ${JSON.stringify(detail)}`);
  }
  return res.json() as Promise<T>;
}

/** Ask for a sign-in code. Always resolves, whether or not the address exists. */
export const requestLoginCode = (email: string) =>
  post<{ ok: boolean }>('/v1/auth/web/email/start', { email });

export const verifyLoginCode = (email: string, code: string) =>
  post<{ token: string }>('/v1/auth/web/email/verify', { email, code });

/** Exchange a Telegram Login Widget payload for a session. */
export const loginWithTelegram = (payload: Record<string, unknown>) =>
  post<{ token: string }>('/v1/auth/web/telegram', payload);

export function loginErrorMessage(raw: string, t: (k: string) => string, fallback: string): string {
  if (raw.includes('code_expired')) return t('login.errExpired');
  if (raw.includes('too_many_attempts') || raw.includes('too_many_codes')) {
    return t('login.errTooMany');
  }
  if (raw.includes('code_invalid') || raw.includes('403')) return t('login.errCode');
  if (raw.includes('no_account')) return t('login.errNoAccount');
  if (raw.includes('blocked')) return t('login.errBlocked');
  return fallback;
}
